import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as actions from '../../actions'
import { doTrack } from '../../helpers/TaggingSerializer'

import LoadingError from '../../components/LoadingError/LoadingError'
import Sidebar from '../../components/Sidebar/Sidebar'
import Meta from '../../components/Meta/Meta'
import FAQAccordions from '../../components/FAQAccordions/FAQAccordions'
import StarRating from '../../components/StarRating/StarRating'
import LiveChat from '../../components/LiveChat/LiveChat'
import SearchBar from '../../components/SearchBar/SearchBar'

import './ProductCare.scss'

class ProductCare extends PureComponent {
  static waitForDataServer (dispatch, params, req, res) {
    return Promise
      .all([
        dispatch(actions.fetchSidebarNav(true)),
        dispatch(actions.fetchPageContent('product-care', true))
      ])
      .then(([sidebar, page]) => {
        if (page.payload.id === 0) {
          res.statusCode = 404
        }
      })
  }

  static waitForDataClient (store) {
    return (nextState, transition, callback) => {
      const { dispatch, getState } = store
      const state = getState()
      const page = state.page.page

      if (page && page.permalink === '/help/product-care/') {
        return callback()
      }

      dispatch(actions.fetchPageContent('product-care'))
        .then(() => { callback() })
        .catch(() => { callback() })
    }
  }

  componentDidMount () {
    const { actions, sidebar } = this.props

    if (!sidebar.length) actions.fetchSidebarNav()
    this.fireAnalytics()
  }

  fireAnalytics = () => {
    if (window.digitalData) {
      window.digitalData.page.pageInfo.pageName = 'ar:help:page:productcare'
      window.digitalData.page.pageInfo.templateTitle = 'help_PAGE_productcare'
    }

    doTrack('help_initialLoad')
  }

  render () {
    const { sidebar, page, error } = this.props
    const ccn = 'product-care'

    if (!page || error) return <LoadingError />

    return (
      <div className={`${ccn} helpfaq font-standard page`}>
        <div id='helpfaqcontent' tabIndex='-1' />

        <Meta pagemeta={page} />

        <div className='container sticky-nav'>
          <div className='xs-row xs-row--negative lg-row'>
            <Sidebar activePage='/help/product-care/' links={sidebar} sticky />
          </div>
        </div>

        <div className='sticky-content'>
          <div className={`${ccn}__header`}>
            <div className='container'>
              <div className='xs-row'>
                <div className='xs-12 lg-9 lg-3-offset'>
                  <h1 className='sr-only'>Product Care</h1>

                  <div className='title'>
                    We'll cover it,<br />
                    so you don't have to
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`${ccn}__section container`}>
            <div className='xs-row'>
              <div className='xs-12 lg-9 lg-3-offset'>
                <h2 className={`${ccn}__subheading h2`}>What's important to you, is important to us</h2>
                <p>Once you've got your new item from Argos, we know you'll never want to be without it again. So why not get that extra bit of cover with Argos Product Care?</p>

                <div className={`${ccn}__cover-types-section xs-row xs-row--negative`}>
                  <div className={`${ccn}-cover-type ${ccn}-cover-type--breakdown xs-12 md-4`}>
                    <img className={`${ccn}-cover-type__image`} src='/helpfaq-assets/static/img/product-care/cover-breakdown.jpg' alt='' />
                    <div className={`${ccn}-cover-type__content`}>
                      <div className={`${ccn}-cover-type__title`}>Breakdown Cover</div>
                      <p className={`${ccn}-cover-type__description`}>If your product breaks down, or you break it by accident, don't worry. We'll sort out a repair, replace it or give you the equivalent value in Argos vouchers. So you'll be back up-and-running in no time.</p>
                      <p className={`${ccn}-cover-type__description`}>And the even better news?</p>
                      <p className={`${ccn}-cover-type__description`}>Pay all in advance in one go or in 10 monthly instalments by Direct Debit.</p>
                      <div className={`${ccn}-cover-type__link-wrapper`}>
                        <a className={`${ccn}-cover-type__link button-link button-link--large`} href='/help/breakdown-care/'>Argos Breakdown Care</a>
                      </div>
                      <div className={ccn + '-cover-type__link-wrapper'}>
                        <a className={`${ccn}-cover-type__link button-link button-link--large`} href='/help/monthly-care/'>Argos Monthly Care</a>
                      </div>
                    </div>
                  </div>

                  <div className={`${ccn}-cover-type ${ccn}-cover-type--replacement xs-12 md-4`}>
                    <img className={`${ccn}-cover-type__image`} src='/helpfaq-assets/static/img/product-care/cover-replacement.jpg' alt='' />
                    <div className={`${ccn}-cover-type__content`}>
                      <div className={`${ccn}-cover-type__title`}>Replacement Care</div>
                      <p className={`${ccn}-cover-type__description`}>With our Replacement Care, there's no need to wait around while your item is sent off to be repaired. We'll just replace it with a like-for-like version. No fuss.</p>
                      <p className={`${ccn}-cover-type__description`}>So whether it's broken down - or just broken by accident - we'll replace it with a brand new one before you even notice it's gone...</p>
                      <div className={`${ccn}-cover-type__link-wrapper`}>
                        <a className={`${ccn}-cover-type__link button-link button-link--large`} href='/help/replacement-care/'>Argos Replacement Care</a>
                      </div>
                    </div>
                  </div>

                  <div className={`${ccn}-cover-type ${ccn}-cover-type--specialist xs-12 md-4`}>
                    <img className={`${ccn}-cover-type__image`} src='/helpfaq-assets/static/img/product-care/cover-watch.jpg' alt='' />
                    <div className={`${ccn}-cover-type__content`}>
                      <div className={`${ccn}-cover-type__title`}>Specialist Cover</div>
                      <p className={`${ccn}-cover-type__description`}>Our tailored product care policies ensure experts are on hand to fix or replace your furniture. We'll also make sure those special jewellery and watch items are replaced in an instant (excluding smart watches).</p>
                      <div className={`${ccn}-cover-type__link-wrapper`}>
                        <a className={`${ccn}-cover-type__link button-link button-link--large`} href='/help/jewellery-watch-care/'>Argos Jewellery &amp; Watch Care</a>
                      </div>
                      <div className={`${ccn}-cover-type__link-wrapper`}>
                        <a className={`${ccn}-cover-type__link button-link button-link--large`} href='/help/furniture-care/'>Argos Furniture Care</a>
                      </div>
                    </div>
                  </div>
                </div>

                <p>That's why our range of Argos Product Care policies make a great addition to your standard manufacturer's guarantee...</p>

                <div className={`${ccn}__benefits-section xs-row xs-row--negative`}>
                  <div className={`${ccn}__benefit xs-12 lg-6`}>
                    <span className='font-bold'>Faults and breakdowns</span> &mdash; once your standard guarantee ends, we'll cover your item against fault and breakdowns
                  </div>

                  <div className={`${ccn}__benefit xs-12 lg-6`}>
                    <span className='font-bold'>Accidental damage</span> &mdash; At Argos we know that sometimes life just happens... which is why we'll fix or replace your item even if it's broken by accident depending on your cover
                  </div>
                </div>

                <h2 className={`${ccn}__subheading h2`}>There's still time... 45 days in fact</h2>
                <div className='xs-row xs-row--negative'>
                  <div className={`${ccn}__45days-image xs-12 md-4`}>
                    <img src='/helpfaq-assets/static/img/product-care/45-days.png' width='110' alt='' />
                  </div>

                  <div className='xs-12 md-8'>
                    <p>Got something from Argos but not quite sure if you want Product Care? You've got 45 days from the date you bought it to get that reassurance with Argos Product Care.</p>
                    <p>Just pop into store, with your receipt and one of our friendly colleagues will be happy to help. You can buy it right-then-and-there and you'll be covered right away. Phew, that's a relief!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='background-color-grey-light'>
            <div className={`${ccn}__section container`}>
              <div className='xs-row'>
                <div className='xs-12 lg-9 lg-3-offset'>
                  <h2 className={`${ccn}__subheading h2`}>Making a claim - we're here for you</h2>
                  <p>The true test of any insurance is when you need to make a claim. With Argos Product Care, it couldn't be simpler.</p>
                  <p>It's quick and simple to make a claim or a complaint – just check the relevant leaflet on the Product Care page from the links above</p>
                </div>
              </div>
            </div>
          </div>

          <div className={`${ccn}__section container`}>
            <div className='xs-row'>
              <div className='xs-12 lg-9 lg-3-offset'>
                <h2 className={`${ccn}__subheading h2`}>
                  Buy yourself more time with the Argos Card.<br />
                  Representative 29.9% APR Variable
                </h2>
                <div className={`${ccn}__afs-promo xs-row xs-row--negative`}>
                  <div className='xs-12 md-7'>
                    <p>Don't forget you can pay for Product Care with the Argos Card (as well as the item you're actually buying). You even could roll both onto one Buy Now Pay Later plan to spread the cost.</p>
                    <div className={`${ccn}__link--large`}>
                      <a className='button-link button-link--large' href='/help/argos-card/'>Apply for an Argos Card</a>
                    </div>
                    <div className={`${ccn}__link--large`}>
                      <a className='button-link button-link--large' href='/'>Shop now with my Argos Card</a>
                    </div>
                  </div>

                  <div className={`${ccn}__45days-image xs-12 md-5`}>
                    <img src='/helpfaq-assets/static/img/product-care/argos-card.png' width='230' alt='' />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='background-color-grey-light'>
            <div className={`${ccn}__section ${ccn}__section--legal container`}>
              <div className='xs-row'>
                <div className='xs-12 lg-9 lg-3-offset'>
                  <p className='font-base--small'>You won't be charged any interest if you pay off your plan within the Pay Later period and keep your account up to date. If you don't pay off your plan before it ends, you'll be charged interest at your normal plan rate from the date of purchase. However, if you choose to make payments without clearing the balance, the earlier you pay the less interest you'll be charged. Credit subject to status.</p>
                  <p className='font-base--small'>Argos Limited is authorised and regulated by the Financial Conduct Authority as a credit broker, and works exclusively with the Argos Card lender, Home Retail Group Card Services Limited.</p>
                  <br />
                  <p className='font-base--small'>Argos Monthly Care, Breakdown Care, Jewellery &amp; Watch Care and Replacement Care are provided by Domestic &amp; General Insurance PLC. Registered Office: Swan Court, 11 Worple Road, Wimbledon, London SW19 4JS. Registered in England and Wales. Company No. 485850.</p>
                  <p className='font-base--small'>Domestic &amp; General Insurance PLC is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority and the Prudential Regulation Authority.</p>
                  <p className='font-base--small'>Argos Furniture Care is provided by Acasta European Insurance Company Limited and administered by Castelan Limited. Registered Office: Castelan Ltd, Alpha House, Sunnyside Road North, Weston-super-Mare, North Somerset, BS23 3QY, United Kingdom. Company No. 7637133.</p>
                  <p className='font-base--small'>Castelan Limited is authorised and regulated by the Financial Conduct Authority – registration number 572287.</p>
                </div>
              </div>
            </div>
          </div>

          <div className='container'>
            <div className='xs-row lg-row'>
              <div className='xs-12 lg-9 lg-3-offset'>

                <FAQAccordions faqs={page.related_faqs} />

                <StarRating pageId={page.id} />

                <LiveChat
                  actions={this.props.actions}
                  chatHasEnded={this.props.chatHasEnded}
                  chatLoading={this.props.chatLoading}
                  chatOnline={this.props.chatOnline}
                />

                <SearchBar position='bottom' />

              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

ProductCare.propTypes = {
  sidebar: PropTypes.array,
  page: PropTypes.object,
  error: PropTypes.bool,
  actions: PropTypes.object,
  chatHasEnded: PropTypes.bool,
  chatLoading: PropTypes.bool,
  chatOnline: PropTypes.bool
}

function mapStateToProps (state) {
  return {
    sidebar: state.help.sidebar,
    page: state.page.page,
    error: state.page.pageError,
    chatHasEnded: state.page.chatHasEnded,
    chatLoading: state.page.chatLoading,
    chatOnline: state.page.chatOnline
  }
}

function mapDispatchToProps (dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductCare)
